import { get, isEmpty } from "lodash";

// Utilities & Constants
import LocalStoreManager from "@/utilities/local-store-manager";
import { getAppFeatureProp } from "@/utilities/app-feature-util";
import vueStore from "@/store";

// Proxies
import GetAccountingQuestionnaireProxy from "@/proxies/back-end/company-user/get-questionnaire";
import GetCompanyUserByQuestionnaireTokenProxy from "@/proxies/back-end/company-user/get-by-questionnaire-token";

const hasAccountingSubscriptionActive = async (company) => {
  const platformConfig = vueStore.state.configModule.platformConfig;

  const admin_constants = getAppFeatureProp(platformConfig.cmsGeneralFeatureList, "admin_constants");
  const ACCOUNTING_SERVICES = getAppFeatureProp(admin_constants.props, "ACCOUNTING_SERVICES");

  const accountingServices = ACCOUNTING_SERVICES.value || [];

  const MAP_CONSTANTS = getAppFeatureProp(platformConfig.cmsGeneralFeatureList, "MAPS_CONSTANTS");
  const SUBSCRIPTION_STATUSES = getAppFeatureProp(MAP_CONSTANTS.props, "SUBSCRIPTION_STATUSES");

  const ACTIVE = get(SUBSCRIPTION_STATUSES, "value.ACTIVE", "active");

  let hasValidAccountingSubscription = null;

  if (company && company.subscriptions) {
    hasValidAccountingSubscription = company.subscriptions.find(
      (subscription) => subscription.status === ACTIVE && accountingServices.includes(subscription.service)
    );
  }

  return !isEmpty(hasValidAccountingSubscription);
};

async function hasAccountingSetupAccess(companyId) {
  const platformConfig = vueStore.state.configModule.platformConfig;
  const store = new LocalStoreManager();
  const getAccountingQuestionnaireProxy = new GetAccountingQuestionnaireProxy();
  const accountingQuestionnaire = await getAccountingQuestionnaireProxy.getAccountingQuestionnaire({
    companyId,
    authToken: new LocalStoreManager().getToken(),
  });

  let currentUser = await store.getUserId();

  let questionnaireUserCompatibility;

  let questionnaireUser = get(accountingQuestionnaire, "recipient_list", []);

  const checkTenant = get(platformConfig, "tenant");
  const isValidQuestionnaireUser = questionnaireUser.find((data) => data._id === currentUser);

  if (questionnaireUser && !isEmpty(isValidQuestionnaireUser) && checkTenant === "hk") {
    questionnaireUserCompatibility = true;
  } else {
    questionnaireUserCompatibility = checkTenant !== "hk";
  }

  return questionnaireUserCompatibility;
}

async function getCompanyUserByQuestionnaireToken(questionnaireToken) {
  const getCompanyUserProxy = new GetCompanyUserByQuestionnaireTokenProxy();
  return await getCompanyUserProxy.getUser({ questionnaireToken });
}

const hasAccountingAccessViaCompanyStatus = (company) => {
  const platformConfig = vueStore.state.configModule.platformConfig;
  const documentSubmissionFeature = getAppFeatureProp(platformConfig.cmsAppFeatures, "document_submission");
  const accessViaCompanyStatus = getAppFeatureProp(documentSubmissionFeature.props, "access_via_company_status");

  if (
    !get(accessViaCompanyStatus, "enabled", false) ||
    !get(accessViaCompanyStatus, "value", []).includes(company.status)
  )
    return false;

  const admin_constants = getAppFeatureProp(platformConfig.cmsGeneralFeatureList, "admin_constants");
  const ACCOUNTING_SERVICES = getAppFeatureProp(admin_constants.props, "ACCOUNTING_SERVICES");
  const accountingServices = ACCOUNTING_SERVICES.value || [];
  // Check for accounting subscription is active (incl. past purchase)
  return company.subscriptions.find((subscription) => accountingServices.includes(subscription.service));
};

const hasSoleTraderOrIndividualTaxReturn = (subscriptions) => {
  const platformConfig = vueStore.state.configModule.platformConfig;
  const ACCOUNTING_SETUP = getAppFeatureProp(platformConfig.cmsAppFeatures, "accounting_set_up");
  const isSoleTraderIndividualTaxReturnEnabled = get(
    getAppFeatureProp(ACCOUNTING_SETUP.props, "is_camunda_wf_disabled_sole_trader"),
    "enabled",
    false
  );

  if (isSoleTraderIndividualTaxReturnEnabled) {
    const individualSoleTraderTaxReturn = get(
      getAppFeatureProp(ACCOUNTING_SETUP.props, "accounting_setup_package_props"),
      "value.individualSoleTraderTaxReturn",
      []
    );
    const isIndividualTaxReturnOrSoleTrader = subscriptions.find(
      (subscription) => individualSoleTraderTaxReturn.includes(subscription.service) && subscription.status === "active"
    );
    return !!isIndividualTaxReturnOrSoleTrader;
  }
  return false;
};

const hasSoleTraderTaxAndAccountingPlan = subscriptions => {
  const platformConfig = vueStore.state.configModule.platformConfig;
  const ACCOUNTING_SETUP = getAppFeatureProp(platformConfig.cmsAppFeatures, "accounting_set_up");
  const soleTraderTaxAndAccountingPlan = get(
    getAppFeatureProp(ACCOUNTING_SETUP.props, "accounting_setup_package_props"),
    "value.soleTraderTaxAndAccounting",
    []
  );
  const isSoleTraderTaxAndAccountingPlan = subscriptions.find(
    subscription => soleTraderTaxAndAccountingPlan.includes(subscription.service) && subscription.status === "active"
  );
  return !!isSoleTraderTaxAndAccountingPlan;
};

export {
  hasAccountingSetupAccess,
  hasAccountingSubscriptionActive,
  getCompanyUserByQuestionnaireToken,
  hasAccountingAccessViaCompanyStatus,
  hasSoleTraderOrIndividualTaxReturn,
  hasSoleTraderTaxAndAccountingPlan
};
